import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, Trophy, Check, X, Zap, Target, Clock, Loader, Volume2,
} from "lucide-react";
import "../styles/wordRelationsGame.css";
import { API_BASE } from "../config";
import gameConfig from "../data/gameConfig.json";
import apiFetch from "../apiFetch";

const CFG = gameConfig.synonyms;

const KEYS = ["A", "B", "C", "D"];

const SynonymsGame = () => {
  const navigate = useNavigate();
  const firstOptionRef = useRef(null);

  const [gameState, setGameState]     = useState("loading"); // loading | playing | feedback | finished
  const [vocabulary, setVocabulary]   = useState([]);
  const [userElo, setUserElo]         = useState(null);
  const [question, setQuestion]       = useState(null);      // { word, definition, tier, options, correctAnswer }
  const [selected, setSelected]       = useState(null);
  const [correct, setCorrect]         = useState(0);
  const [wrong, setWrong]             = useState(0);
  const [streak, setStreak]           = useState(0);
  const [answered, setAnswered]       = useState(0);
  const [startTime]                   = useState(Date.now());
  const [eloLog, setEloLog]           = useState([]);
  const [coinsEarned, setCoinsEarned] = useState(0);
  const [speaking, setSpeaking]       = useState(false);

  /* ── Cancel speech on unmount ── */
  useEffect(() => () => window.speechSynthesis.cancel(), []);

  const speak = useCallback((question) => {
    window.speechSynthesis.cancel();
    const text = `Which word means the same as ${question.word}? It means: ${question.definition}. `
      + question.options.map((opt, i) => `${KEYS[i]}: ${opt}`).join(". ");
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate = 0.88;
    utt.onstart = () => setSpeaking(true);
    utt.onend = () => setSpeaking(false);
    utt.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utt);
  }, []);

  /* ── Load vocab + ELO on mount ── */
  useEffect(() => {
    (async () => {
      try {
        const userId = parseInt(localStorage.getItem("userId"), 10);
        const [eloRes, vocabRes] = await Promise.all([
          apiFetch(`/api/synonyms/current-elo?userId=${userId}`),
          apiFetch(`/api/vocabulary`),
        ]);
        const { elo } = await eloRes.json();
        const { words } = await vocabRes.json();
        setUserElo(elo ?? CFG.defaultElo);
        setVocabulary(words);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  /* ── Generate a question only when the game is waiting for one ── */
  useEffect(() => {
    if (
      vocabulary.length > 0 &&
      userElo !== null &&
      gameState === "loading"
    ) {
      generateQuestion(vocabulary, userElo);
    }
  }, [vocabulary, userElo, gameState]);

  /* ── Focus the first option whenever a new question appears ── */
  useEffect(() => {
    if (gameState === "playing") {
      firstOptionRef.current?.focus();
    }
  }, [question, gameState]);

  /* ── Pick a question from the available pool ── */
  const generateQuestion = useCallback((vocab, elo) => {
    const tier      = Math.max(1, Math.min(29, Math.ceil(elo / 100)));
    const tierWords = vocab.filter((w) => w.tier === tier);
    const eligible  = tierWords.filter((w) => w.synonyms && w.synonyms.length > 0);

    if (eligible.length === 0) {
      setGameState("finished");
      return;
    }

    // Pick a random eligible word
    const word       = eligible[Math.floor(Math.random() * eligible.length)];
    const correct    = word.synonyms[Math.floor(Math.random() * word.synonyms.length)];

    // 3 distractor words from same tier (not synonyms/antonyms of this word)
    const excluded   = new Set([word.word.toLowerCase(), ...(word.synonyms || []).map(s => s.toLowerCase()), ...(word.antonyms || []).map(a => a.toLowerCase())]);
    const distractors = tierWords
      .filter((w) => !excluded.has(w.word.toLowerCase()))
      .sort(() => Math.random() - 0.5)
      .slice(0, 3)
      .map((w) => w.word);

    const options = [correct, ...distractors].sort(() => Math.random() - 0.5);

    window.speechSynthesis.cancel();
    setSpeaking(false);
    setQuestion({ word: word.word, definition: word.definition, tier: word.tier, options, correctAnswer: correct });
    setSelected(null);
    setGameState("playing");
  }, []);

  /* ── Handle option selection ── */
  const handleSelect = (option) => {
    if (gameState !== "playing") return;
    setSelected(option);
    setGameState("feedback");
    setAnswered((n) => n + 1);

    const isCorrect = option === question.correctAnswer;
    const eloChange = isCorrect ? CFG.eloCorrect : -CFG.eloPenalty;
    const newElo    = Math.max(0, userElo + eloChange);

    setUserElo(newElo);
    setEloLog((prev) => [...prev, { word: question.word, change: eloChange }]);

    if (isCorrect) {
      setCorrect((n) => n + 1);
      setStreak((n) => n + 1);
    } else {
      setWrong((n) => n + 1);
      setStreak(0);
    }

    setTimeout(() => generateQuestion(vocabulary, newElo), 1600);
  };

  const finishSession = async () => {
    const userId = parseInt(localStorage.getItem("userId"), 10);
    const coins  = Math.floor(correct * Math.max(CFG.coinsEloMin, userElo / CFG.coinsEloScale));
    setCoinsEarned(coins);
    setGameState("finished");

    try {
      await apiFetch(`/api/synonyms/save-elo`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, finalElo: userElo }),
      });
      if (coins > 0) {
        await apiFetch(`/api/resources/update-coins`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, delta: coins }),
        });
        await apiFetch(`/api/resources/xp/add`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, delta: coins * CFG.xpMultiplier }),
        });
      }
    } catch (e) { console.error(e); }
  };

  const formatTime = (ms) => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  /* ── Loading ── */
  if (gameState === "loading" || userElo === null) {
    return (
      <div className="wr-loading-screen">
        <div className="wr-loading-card">
          <Loader className="wr-spinner" />
          <p>Loading Synonyms Game…</p>
        </div>
      </div>
    );
  }

  /* ── Results ── */
  if (gameState === "finished") {
    const accuracy = answered > 0 ? Math.round((correct / answered) * 100) : 0;
    return (
      <div className="wr-results-screen">
        <div className="wr-results-card">
          <Trophy className="wr-results-icon" />
          <h2>Session Complete!</h2>
          <p className="wr-results-sub">Synonyms Game</p>
          <div className="wr-results-stats">
            <div className="wr-stat">
              <Target className="wr-stat-icon" />
              <div className="wr-stat-value">{answered}</div>
              <div className="wr-stat-label">Answered</div>
            </div>
            <div className="wr-stat">
              <Check className="wr-stat-icon" style={{ color: "#16a34a" }} />
              <div className="wr-stat-value">{accuracy}%</div>
              <div className="wr-stat-label">Accuracy</div>
            </div>
            <div className="wr-stat">
              <Clock className="wr-stat-icon" />
              <div className="wr-stat-value">{formatTime(Date.now() - startTime)}</div>
              <div className="wr-stat-label">Time</div>
            </div>
          </div>
          <div className="wr-elo-summary">
            Final ELO: <strong>{userElo}</strong> &nbsp;·&nbsp; Coins earned: <strong>{coinsEarned}</strong>
          </div>
          <div className="wr-results-actions">
            <button className="wr-btn-primary" onClick={() => { setGameState("loading"); setCorrect(0); setWrong(0); setAnswered(0); setStreak(0); setEloLog([]); generateQuestion(vocabulary, userElo); }}>
              <RefreshCw size={15} /> Play Again
            </button>
            <button className="wr-btn-secondary" onClick={() => navigate("/games/vocabulary")}>
              <ArrowLeft size={15} /> Back to Games
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Main game ── */
  return (
    <div className="wr-game">
      {/* Header */}
      <div className="wr-header">
        <div className="wr-header-left">
          <h1>Synonyms</h1>
          <div className="wr-elo-badge">
            <Zap size={14} /> {userElo}
          </div>
        </div>
        <div className="wr-header-right">
          <div className="wr-score">
            <span className="wr-score-correct"><Check size={14} /> {correct}</span>
            <span className="wr-score-wrong"><X size={14} /> {wrong}</span>
          </div>
          <button className="wr-finish-btn" onClick={finishSession}>Finish</button>
          <button className="wr-back-btn" onClick={() => navigate("/games/vocabulary")}>
            <ArrowLeft size={14} /> Back
          </button>
        </div>
      </div>

      {/* Question card */}
      <div className="wr-main">
        {question && (
          <div className="wr-card">
            <div className="wr-word-display">
              <p className="wr-word">{question.word}</p>
              <p className="wr-definition">{question.definition}</p>
              <span className="wr-tier-badge">Tier {question.tier}</span>
            </div>

            <div className="wr-question-row">
              <p className="wr-question-text">Which word means the same as "{question.word}"?</p>
              <button
                className={`wr-speak-btn ${speaking ? "speaking" : ""}`}
                onClick={() => speak(question)}
                title="Read question aloud"
              >
                <Volume2 size={18} />
              </button>
            </div>

            <div className="wr-options">
              {question.options.map((opt, i) => {
                let cls = "";
                if (gameState === "feedback") {
                  if (opt === question.correctAnswer)       cls = "correct";
                  else if (opt === selected && opt !== question.correctAnswer) cls = "wrong";
                }
                return (
                  <button
                    key={opt}
                    ref={i === 0 ? firstOptionRef : null}
                    className={`wr-option ${cls}`}
                    onClick={() => handleSelect(opt)}
                    disabled={gameState === "feedback"}
                  >
                    <span className="wr-option-key">{KEYS[i]}</span>
                    {opt}
                  </button>
                );
              })}
            </div>

            <div className="wr-progress-wrap">
              <div className="wr-progress-bar">
                <div className="wr-progress-fill" style={{ width: `${Math.min(100, (correct / Math.max(1, answered)) * 100)}%` }} />
              </div>
              <span className="wr-progress-label">{correct}/{answered} correct</span>
            </div>

            <div className="wr-streak">
              {streak >= 3 ? `🔥 ${streak} streak!` : ""}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SynonymsGame;
